import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Shield, Zap, ChevronRight, Trophy, Activity, Crosshair } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';

const riskColor = {
  low: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Niedrig' },
  medium: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Mittel' },
  high: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Hoch' },
};

const AIRecommendations = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const res = await fetch(`${API_URL}/recommendations/top-tips`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Failed to load recommendations:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="ai-recommendations-loading" className="mb-6 p-4 bg-[#1E1E1E] rounded-xl border border-white/5 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-48 mb-4" />
        <div className="space-y-3">
          <div className="h-20 bg-white/5 rounded-lg" />
          <div className="h-20 bg-white/5 rounded-lg" />
          <div className="h-20 bg-white/5 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!data || !data.recommendations?.length) {
    return null;
  }

  const { recommendations, ai_stats, sport_accuracy } = data;
  const dp = ai_stats?.data_points || 0;

  return (
    <div data-testid="ai-recommendations-widget" className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7B1FA2] to-[#4A148C] flex items-center justify-center">
            <Brain className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">KI Top-Empfehlungen</h2>
            <p className="text-[10px] text-gray-500">
              Basierend auf {dp > 1000000 ? `${(dp / 1000000).toFixed(1)}M` : `${(dp / 1000).toFixed(0)}K`} Datenpunkten
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#00BFA6]/10 border border-[#00BFA6]/20">
            <Crosshair className="w-3 h-3 text-[#00BFA6]" />
            <span className="text-[10px] font-mono font-bold text-[#00BFA6]">PRECISION</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#00BFA6]/10 border border-[#00BFA6]/20">
            <Activity className="w-3 h-3 text-[#00BFA6]" />
            <span className="text-[10px] font-mono text-[#00BFA6]">LIVE</span>
          </div>
        </div>
      </div>

      {/* Recommendations Cards */}
      <div className="space-y-2.5">
        {recommendations.map((rec, idx) => {
          const risk = riskColor[rec.risk] || riskColor.medium;
          return (
            <div
              key={idx}
              data-testid={`recommendation-card-${idx}`}
              className="relative overflow-hidden bg-[#1E1E1E] border border-white/5 rounded-xl p-3.5 hover:border-[#00BFA6]/30 transition-all duration-300 group"
            >
              {/* Rank badge */}
              <div className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                  idx === 1 ? 'bg-gray-400/20 text-gray-400' :
                  'bg-orange-500/20 text-orange-400'
                }`}>
                  #{idx + 1}
                </div>
              </div>

              {/* Main content */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* League & Sport */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 truncate max-w-[140px]">
                      {rec.league}
                    </span>
                    <span className="text-[10px] text-gray-600">{rec.sport}</span>
                  </div>

                  {/* Match */}
                  <p className="text-sm font-medium text-white mb-1 truncate" title={rec.match}>
                    {rec.match}
                  </p>

                  {/* Tip */}
                  <div className="flex items-center gap-2 mb-2">
                    <ChevronRight className="w-3 h-3 text-[#00BFA6]" />
                    <span className="text-xs font-semibold text-[#00BFA6]">{rec.tip}</span>
                  </div>

                  {/* Meta row */}
                  <div className="flex flex-wrap items-center gap-2 text-[10px]">
                    {/* Elo */}
                    <span className="text-gray-500">
                      Elo: {rec.home_elo} vs {rec.away_elo}
                    </span>
                    {/* League Accuracy */}
                    <span className="text-gray-500 flex items-center gap-0.5">
                      <Trophy className="w-2.5 h-2.5" /> Liga: {rec.league_accuracy}%
                    </span>
                    {/* Form */}
                    <div className="flex gap-0.5">
                      {(rec.form || '').split('').map((c, fi) => (
                        <span key={fi} className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center text-[8px] font-bold ${
                          c === 'W' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Confidence + Risk */}
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <div className="text-right">
                    <div className="text-xl font-bold font-mono text-white leading-none">
                      {rec.confidence}%
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5">Konfidenz</div>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${risk.bg} ${risk.text} border ${risk.border}`}>
                    <Shield className="w-2.5 h-2.5 inline mr-0.5" />
                    {risk.label}
                  </div>
                </div>
              </div>

              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none bg-gradient-to-r from-[#00BFA6]/5 to-transparent" />
            </div>
          );
        })}
      </div>

      {/* AI Stats Footer */}
      {ai_stats && (
        <div data-testid="ai-stats-footer" className="mt-3 grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-[#1E1E1E] rounded-lg border border-white/5">
            <div className="text-sm font-bold font-mono text-[#00BFA6]">
              {ai_stats.total_matches_analyzed > 1000 ? `${(ai_stats.total_matches_analyzed / 1000).toFixed(0)}K` : ai_stats.total_matches_analyzed}
            </div>
            <div className="text-[9px] text-gray-500">Matches</div>
          </div>
          <div className="text-center p-2 bg-[#1E1E1E] rounded-lg border border-white/5">
            <div className="text-sm font-bold font-mono text-[#7B1FA2]">
              {ai_stats.total_teams > 1000 ? `${(ai_stats.total_teams / 1000).toFixed(1)}K` : ai_stats.total_teams}
            </div>
            <div className="text-[9px] text-gray-500">Teams</div>
          </div>
          <div className="text-center p-2 bg-[#1E1E1E] rounded-lg border border-white/5">
            <div className="text-sm font-bold font-mono text-white">
              {ai_stats.teams_with_elo > 1000 ? `${(ai_stats.teams_with_elo / 1000).toFixed(1)}K` : ai_stats.teams_with_elo}
            </div>
            <div className="text-[9px] text-gray-500">Elo-Ratings</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRecommendations;
